/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { DcrType } from '@/core/graphql/generated/types';

import { getReviewEntityTitle } from '../../_utils/getReviewEntityTitle';
import { generateReviewEntityRows } from '../_utils/generateReviewEntityRows';

import { ReviewArrayCard } from './ReviewArrayCard/ReviewArrayCard';
import { reviewArrayFieldStyles as sx } from './ReviewArrayField.styles';
import { ReviewDeletedItem } from './ReviewDeletedItem/ReviewDeletedItem';

const INDIVIDUAL_CARD_KEYS = ['addresses', 'licenses', 'parenthcos'];

type Props = {
	fieldKey: string;
	changedItemsMap: Record<string, any> | any[];
	defaultValues: any;
	currentValues: any;
	fieldTranslations: Record<string, string> | undefined;
	dcrType: DcrType;
};

export const ReviewArrayField = ({
	fieldKey,
	changedItemsMap,
	defaultValues,
	currentValues,
	fieldTranslations,
	dcrType
}: Props) => {
	const { t } = useTranslation();

	// Handle both Object (from getDirtyValues) and Array (fallback)
	const entries = Array.isArray(changedItemsMap)
		? changedItemsMap.map((item, idx) => [String(idx), item])
		: Object.entries(changedItemsMap);

	const dirtyEntries = entries.filter(([, changes]) => !!changes);

	if (dirtyEntries.length === 0) return null;

	const oldArray = defaultValues?.[fieldKey] || [];
	const newArray = currentValues?.[fieldKey] || [];
	const isIndividualCard = INDIVIDUAL_CARD_KEYS.includes(fieldKey);

	const renderedBlocks = dirtyEntries.map(([indexStr, changes]) => {
		const index = Number(indexStr);
		const oldItem = oldArray[index];
		const newItem = newArray[index];

		// Deletion
		if (oldItem && !newItem) {
			const name = getReviewEntityTitle(fieldKey, null, oldItem);

			if (isIndividualCard) {
				return (
					<ReviewArrayCard
						key={index}
						rows={[]}
						title={<ReviewDeletedItem isIndividualCard label={name} />}
					/>
				);
			} else {
				return (
					<Box key={index} mb={2}>
						<ReviewDeletedItem label={name} />
					</Box>
				);
			}
		}

		// Update / Add
		if (newItem) {
			// Note(Filip): If this item was simply moved (index changed due to a deletion above it),
			const newItemId = newItem.id || newItem.vid_V;
			if (newItemId) {
				const originalVersion = oldArray.find(
					(o: { id: string; vid_V: string }) =>
						(o?.id || o?.vid_V) === newItemId
				);

				if (originalVersion) {
					// Note(Filip): If we there is an original version of this item and it looks identical to the new one (ignoring internal keys)
					// Then this is just a shift, not a user edit -> We HIDE IT.
					const isShiftOnly =
						JSON.stringify(originalVersion) === JSON.stringify(newItem);
					if (isShiftOnly) return null;
				}
			}

			const validRows = generateReviewEntityRows(
				fieldKey,
				changes,
				oldItem,
				newItem,
				fieldTranslations,
				isIndividualCard,
				dcrType
			);

			// If this is a New Card (no oldItem), force it to display even if rows are empty.
			// (e.g. Affiliations might show the Name in the Title only, leaving rows empty)
			const isNewCard = !oldItem && isIndividualCard;

			if (validRows.length === 0 && !isNewCard) {
				return null;
			}

			if (isIndividualCard) {
				const cardTitle = getReviewEntityTitle(fieldKey, newItem, oldItem);

				return (
					<ReviewArrayCard rows={validRows} title={cardTitle} key={index} />
				);
			}

			return (
				<Box key={index} mb={2}>
					<Box sx={sx.validRowsContainer}>{validRows}</Box>
				</Box>
			);
		}

		return null;
	});

	const validBlocks = renderedBlocks.filter(Boolean);

	if (validBlocks.length === 0) return null;

	if (isIndividualCard) {
		return <Box sx={sx.validCardsContainer}>{validBlocks}</Box>;
	}

	return (
		<Box sx={sx.nestedGroup}>
			<Typography sx={sx.nestedGroupTitle(validBlocks.length > 0)}>
				{fieldTranslations?.[fieldKey] ?? t(fieldKey)}
			</Typography>
			<Divider sx={sx.divider} />
			{validBlocks}
		</Box>
	);
};
